import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import NoShowAlertBanner from '@/components/NoShowAlertBanner';
import { Calendar } from '@/components/ui/calendar';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { businessesAPI, bookingsAPI, servicesAPI, notificationsAPI } from '@/lib/api';
import { hapticLight, hapticMedium, hapticSuccess, hapticError } from '@/lib/haptics';
import { isNativeApp, openExternalUrl, openExternalBookviaFlow, PUBLIC_WEB_URL } from '@/lib/capacitor';
import { formatDate, formatTime, formatCurrency, getStatusColor, getInitials } from '@/lib/utils';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import ReportsTab from '@/components/ReportsTab';
import AgendaTimeline from '@/components/AgendaTimeline';
import { ProfileCompletionBanner } from '@/components/ProfileCompletionBanner';
import BusinessBrandingSection from '@/components/BusinessBrandingSection';
import StripeConnectRequiredBanner from '@/components/StripeConnectRequiredBanner';
import BranchesTab from '@/components/BranchesTab';
import BranchSelector from '@/components/BranchSelector';
import { RevisionRequestBanner } from '@/components/RevisionRequestBanner';
import SubscriptionPastDueBanner from '@/components/SubscriptionPastDueBanner';
import BusinessClientsTab from '@/components/BusinessClientsTab';
import { AdminRoleInfoModal } from '@/components/AdminRoleInfoModal';
import { ShareAdminLinkButton } from '@/components/ShareAdminLinkButton';
import HelpTooltip from '@/components/help/HelpTooltip';
import FloatingHelpButton from '@/components/help/FloatingHelpButton';
import GuidedTour from '@/components/help/GuidedTour';
import TabHelpHeader from '@/components/help/TabHelpHeader';
import { kpiTips, tabTips } from '@/lib/dashboardHelp';
import { isEventsCategoryActive } from '@/lib/featureFlags';
import {
  Calendar as CalendarIcon, DollarSign, Star, Users, Clock, CheckCircle2,
  XCircle, AlertTriangle, TrendingUp, Settings, UserCog, Image, Upload,
  Trash2, Eye, Plus, Pencil, BarChart3, Briefcase, ArrowUpRight,
  Ban, CalendarOff, CreditCard, Shield, RefreshCw, Mail, Phone, History,
  ChevronLeft, ChevronRight, Filter, Bell, ClipboardList, Building2
} from 'lucide-react';

export default function BusinessDashboardPage() {
  const { t, language } = useI18n();
  const { business, user, isAuthenticated, isBusiness, isManager, hasPermission, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardError, setDashboardError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dayBookings, setDayBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [closures, setClosures] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTabRaw] = useState(() => searchParams.get('tab') || 'overview');
  // Active branch filter (Phase D — multi-branch). null = "All branches".
  const [activeBranchId, setActiveBranchId] = useState(() => {
    try { return localStorage.getItem('bookvia-active-branch-id') || null; } catch { return null; }
  });
  // Keep URL ?tab=X in sync with the active tab so deep links from the navbar dropdown work.
  const VALID_TABS = ['overview', 'reports', 'clients', 'services', 'team', 'branches', 'closures', 'photos', 'subscription', 'activity'];
  const setActiveTab = (next) => {
    setActiveTabRaw(next);
    const params = new URLSearchParams(searchParams);
    if (next && next !== 'overview') {
      params.set('tab', next);
    } else {
      params.delete('tab');
    }
    setSearchParams(params, { replace: true });
  };
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);
  const [statsModal, setStatsModal] = useState({ open: false, type: null, title: '', bookings: [], loading: false, totalRevenue: null });
  const [statsDateFrom, setStatsDateFrom] = useState('');
  const [statsDateTo, setStatsDateTo] = useState('');
  const [rescheduleModal, setRescheduleModal] = useState({ open: false, booking: null });
  const [cancelDialog, setCancelDialog] = useState({ open: false, booking: null, step: 'confirm', reason: '', submitting: false, preview: null, previewLoading: false });
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleSlots, setRescheduleSlots] = useState([]);
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [bookingDetail, setBookingDetail] = useState(null);
  const [clientHistory, setClientHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [managerModal, setManagerModal] = useState({ open: false, worker: null });
  const [managerPermissions, setManagerPermissions] = useState({});
  const [pinModal, setPinModal] = useState({ open: false, type: null }); // type: 'owner_setup' | 'manager_pin'
  const [pinValue, setPinValue] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [ownerHasPin, setOwnerHasPin] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityPages, setActivityPages] = useState(1);
  const [activityFilter, setActivityFilter] = useState('all');
  const [activityLoading, setActivityLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [dashSummary, setDashSummary] = useState(null);
  const [profileCompletion, setProfileCompletion] = useState(null);
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !isBusiness) {
      navigate('/business/login');
      return;
    }
    loadDashboard();
    loadNotifications();
    const notifInterval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(notifInterval);
  }, [isAuthenticated, isBusiness, authLoading]);

  const loadNotifications = async () => {
    try {
      const [res, countRes] = await Promise.all([
        notificationsAPI.getAll(),
        notificationsAPI.getUnreadCount()
      ]);
      setNotifications(Array.isArray(res.data) ? res.data : []);
      setUnreadCount(countRes.data?.count || 0);
    } catch { /* ignore */ }
  };

  const loadUnreadCount = async () => {
    try {
      const res = await notificationsAPI.getUnreadCount();
      setUnreadCount(res.data?.count || 0);
    } catch { /* ignore */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  // Close notification panel on outside click
  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e) => {
      if (!e.target.closest('[data-testid="notification-bell"]') && !e.target.closest('[data-testid="notification-panel"]')) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [notifOpen]);

  useEffect(() => {
    loadDashboard();
    loadDayBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId]);

  // React to URL ?tab=X changes (e.g., when user clicks navbar dropdown while already on this page)
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && VALID_TABS.includes(tab) && tab !== activeTab) {
      setActiveTabRaw(tab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (isManager && activeTab === 'overview' && !hasPermission('view_agenda')) {
      const fallbackTabs = ['services', 'team', 'photos'];
      const fallbackPerms = { services: 'edit_services', team: 'view_team', photos: 'edit_photos' };
      const first = fallbackTabs.find(t => hasPermission(fallbackPerms[t]));
      if (first) setActiveTab(first);
    }
  }, [isManager]);

  useEffect(() => {
    if (dashboardData?.business?.id) {
      loadDayBookings();
    }
  }, [selectedDate, dashboardData]);

  useEffect(() => {
    if (activeTab === 'activity' && !isManager) {
      loadActivityLogs(1, activityFilter);
    }
  }, [activeTab]);

  const loadDashboard = async () => {
    try {
      setDashboardError(null);
      // Step 1: load the dashboard first — we need `business.id` from its response
      // to safely call `services/business/{id}`. Calling with an empty id
      // (`/services/business/`) results in 307 → 405 Method Not Allowed.
      const dashRes = await businessesAPI.getDashboard({ branch_id: activeBranchId || undefined });
      setDashboardData(dashRes.data);
      const bizId = dashRes?.data?.business?.id || business?.id || user?.business_id || '';

      // Step 2: load remaining data in parallel now that we have a valid bizId
      const [servicesRes, workersRes, photosRes, closuresRes] = await Promise.all([
        bizId
          ? servicesAPI.getByBusiness(bizId).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
        businessesAPI.getMyWorkers().catch(() => ({ data: [] })),
        businessesAPI.getMyPhotos().catch(() => ({ data: [] })),
        businessesAPI.getMyClosures().catch(() => ({ data: [] })),
      ]);
      setServices(Array.isArray(servicesRes.data) ? servicesRes.data : []);
      setWorkers(Array.isArray(workersRes.data) ? workersRes.data : []);
      setPhotos(Array.isArray(photosRes.data) ? photosRes.data : []);
      setClosures(Array.isArray(closuresRes.data) ? closuresRes.data : []);
      // Load subscription info
      try {
        const subRes = await businessesAPI.getSubscriptionStatus();
        setSubscriptionData(subRes.data);
      } catch { setSubscriptionData(null); }
      // Load pin status
      try {
        const pinRes = await businessesAPI.getPinStatus();
        setOwnerHasPin(pinRes.data?.has_pin || false);
      } catch { setOwnerHasPin(false); }
      // Load dashboard summary
      try {
        const sumRes = await businessesAPI.getDashboardSummary();
        setDashSummary(sumRes.data);
      } catch { setDashSummary(null); }
      // Phase 14: profile completion checklist
      try {
        const compRes = await businessesAPI.getProfileCompletion();
        setProfileCompletion(compRes.data);
      } catch { setProfileCompletion(null); }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      const detail = error?.response?.data?.detail || error?.message || 'Error desconocido';
      setDashboardError(detail);
      toast.error(language === 'es' ? `Error al cargar el panel: ${detail}` : `Dashboard error: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const loadDayBookings = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const res = await bookingsAPI.getBusiness({ date: dateStr, branch_id: activeBranchId || undefined });
      setDayBookings(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error loading bookings:', error);
      setDayBookings([]);
    }
  };

  const loadActivityLogs = async (page = 1, filter = 'all') => {
    setActivityLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filter === 'admin') params.actor_type = 'admin';
      if (filter === 'owner') params.actor_type = 'owner';
      const res = await businessesAPI.getActivityLog(params);
      setActivityLogs(res.data?.logs || []);
      setActivityTotal(res.data?.total || 0);
      setActivityPages(res.data?.pages || 1);
      setActivityPage(page);
    } catch (error) {
      console.error('Error loading activity logs:', error);
      setActivityLogs([]);
    } finally {
      setActivityLoading(false);
    }
  };

  const handleBookingAction = async (bookingId, action) => {
    // Cancellation now goes through a confirmation dialog with reason.
    // Use openCancelDialog(booking) instead of action='cancel'.
    if (action === 'cancel') {
      hapticMedium();
      const bk = dayBookings.find(b => b.id === bookingId);
      if (bk) openCancelDialog(bk);
      return;
    }
    try {
      switch (action) {
        case 'confirm': await bookingsAPI.confirm(bookingId); break;
        case 'complete': await bookingsAPI.complete(bookingId); break;
        case 'no-show': await bookingsAPI.markNoShow(bookingId); break;
        default: break;
      }
      hapticSuccess();
      toast.success(language === 'es' ? 'Actualizado' : 'Updated');
      loadDayBookings();
      loadDashboard();
    } catch (error) {
      hapticError();
      const detail = error?.response?.data?.detail || '';
      toast.error(language === 'es' ? `Error al actualizar: ${detail}` : `Error updating: ${detail}`);
    }
  };

  const openCancelDialog = (booking) => {
    setCancelDialog({ open: true, booking, step: 'confirm', reason: '', submitting: false, preview: null, previewLoading: true });
    bookingsAPI.getCancellationPreview(booking.id)
      .then((res) => {
        setCancelDialog((s) => (s.booking?.id === booking.id ? { ...s, preview: res.data, previewLoading: false } : s));
      })
      .catch(() => {
        setCancelDialog((s) => (s.booking?.id === booking.id ? { ...s, preview: null, previewLoading: false } : s));
      });
  };

  const submitBusinessCancellation = async () => {
    const bk = cancelDialog.booking;
    const reason = (cancelDialog.reason || '').trim();
    if (!bk) return;
    if (reason.length < 5) {
      toast.error(language === 'es' ? 'Por favor explica el motivo (minimo 5 caracteres)' : 'Please explain the reason (minimum 5 characters)');
      return;
    }
    setCancelDialog((s) => ({ ...s, submitting: true }));
    try {
      await bookingsAPI.cancelByBusiness(bk.id, reason);
      hapticSuccess();
      toast.success(language === 'es' ? 'Cita cancelada. El cliente fue notificado.' : 'Booking cancelled. El cliente fue notificado.');
      setCancelDialog({ open: false, booking: null, step: 'confirm', reason: '', submitting: false, preview: null, previewLoading: false });
      // Also close the detail modal if it was open
      setBookingDetail(null);
      setClientHistory(null);
      loadDayBookings();
      loadDashboard();
    } catch (error) {
      hapticError();
      const detail = error?.response?.data?.detail || '';
      toast.error(language === 'es' ? `Error al cancelar: ${detail}` : `Error cancelling: ${detail}`);
      setCancelDialog((s) => ({ ...s, submitting: false }));
    }
  };

  const openReschedule = (booking) => {
    setRescheduleModal({ open: true, booking });
    setRescheduleDate(null);
    setRescheduleSlots([]);
    setRescheduleTime('');
  };

  const loadRescheduleSlots = async (date) => {
    const bk = rescheduleModal.booking;
    if (!bk || !date) return;
    setSlotsLoading(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const res = await bookingsAPI.getAvailability(bk.business_id, dateStr, bk.service_id, bk.worker_id);
      const available = (res.data.slots || []).filter(s => s.status === 'available');
      setRescheduleSlots(available);
    } catch {
      setRescheduleSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleReschedule = async () => {
    const bk = rescheduleModal.booking;
    if (!bk || !rescheduleDate || !rescheduleTime) return;
    setRescheduleLoading(true);
    try {
      const dateStr = format(rescheduleDate, 'yyyy-MM-dd');
      await bookingsAPI.rescheduleByBusiness(bk.id, { new_date: dateStr, new_time: rescheduleTime });
      toast.success(language === 'es' ? 'Cita reagendada exitosamente' : 'Appointment rescheduled');
      setRescheduleModal({ open: false, booking: null });
      loadDayBookings();
      loadDashboard();
    } catch (error) {
      const detail = error?.response?.data?.detail || '';
      toast.error(language === 'es' ? `Error al reagendar: ${detail}` : `Reschedule error: ${detail}`);
    } finally {
      setRescheduleLoading(false);
    }
  };

  const openStatsModal = async (type, title, dateFrom, dateTo) => {
    setStatsModal({ open: true, type, title, bookings: [], loading: true, totalRevenue: null });
    try {
      const res = await bookingsAPI.getStatsDetail(type, dateFrom || undefined, dateTo || undefined);
      setStatsModal(prev => ({ 
        ...prev, 
        bookings: res.data.bookings || [], 
        loading: false,
        totalRevenue: res.data.total_revenue 
      }));
    } catch {
      setStatsModal(prev => ({ ...prev, bookings: [], loading: false }));
      toast.error(language === 'es' ? 'Error al cargar datos' : 'Error loading data');
    }
  };

  const closeStatsModal = () => {
    setStatsModal(prev => ({ ...prev, open: false }));
    // Refresh dashboard stats
    loadDashboard();
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const MAX_PHOTOS = 10;
    if (photos.length >= MAX_PHOTOS) {
      toast.error(language === 'es' ? `Maximo ${MAX_PHOTOS} fotos permitidas` : `Maximum ${MAX_PHOTOS} photos allowed`);
      e.target.value = '';
      return;
    }
    const remaining = MAX_PHOTOS - photos.length;
    const filesToUpload = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.warning(language === 'es' ? `Solo puedes subir ${remaining} foto(s) mas` : `You can only upload ${remaining} more photo(s)`);
    }
    setUploading(true);
    let successCount = 0;
    for (const file of filesToUpload) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: ${language === 'es' ? 'Maximo 5MB' : 'Max 5MB'}`);
        continue;
      }
      try {
        await businessesAPI.uploadPhoto(file);
        successCount++;
      } catch (err) {
        const detail = err.response?.data?.detail || '';
        if (detail.includes('Maximum')) {
          toast.error(language === 'es' ? `Maximo ${MAX_PHOTOS} fotos permitidas` : detail);
          break;
        }
        toast.error(`${file.name}: ${language === 'es' ? 'Error al subir' : 'Upload failed'}`);
      }
    }
    if (successCount > 0) {
      toast.success(language === 'es' ? `${successCount} foto(s) subida(s)` : `${successCount} photo(s) uploaded`);
      const photosRes = await businessesAPI.getMyPhotos().catch(() => ({ data: [] }));
      setPhotos(Array.isArray(photosRes.data) ? photosRes.data : []);
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar esta foto?' : 'Delete this photo?')) return;
    try {
      await businessesAPI.deletePhoto(photoId);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      toast.success(language === 'es' ? 'Foto eliminada' : 'Photo deleted');
    } catch {
      toast.error(language === 'es' ? 'Error' : 'Error');
    }
  };

  // Closure handlers
  const closedDates = closures.map(c => c.date);

  const handleCancelSubscription = async () => {
    if (!window.confirm(language === 'es'
      ? '¿Estás seguro de cancelar tu suscripción? Tu negocio dejará de ser visible al final del periodo actual.'
      : 'Are you sure you want to cancel? Your business will stop being visible at the end of the current period.'
    )) return;
    setCancelingSubscription(true);
    try {
      await businessesAPI.cancelSubscription();
      toast.success(language === 'es' ? 'Suscripción cancelada. Se mantendrá activa hasta el final del periodo.' : 'Subscription canceled. It will remain active until the end of the period.');
      const subRes = await businessesAPI.getSubscriptionStatus();
      setSubscriptionData(subRes.data);
    } catch {
      toast.error(language === 'es' ? 'Error al cancelar' : 'Error canceling');
    } finally {
      setCancelingSubscription(false);
    }
  };

  // ── Manager & PIN Functions ──
  const PERMISSION_LABELS = {
    complete_bookings: { es: 'Completar/confirmar citas', en: 'Complete/confirm bookings' },
    reschedule_bookings: { es: 'Reagendar citas', en: 'Reschedule bookings' },
    cancel_bookings: { es: 'Cancelar citas', en: 'Cancel bookings' },
    block_clients: { es: 'Bloquear clientes', en: 'Block clients' },
    view_client_data: { es: 'Ver datos de contacto del cliente', en: 'View client contact data' },
    edit_services: { es: 'Editar servicios y precios', en: 'Edit services & prices' },
    view_reports: { es: 'Ver ingresos y reportes', en: 'View income & reports' },
    view_today_bookings: { es: 'Ver citas de hoy', en: "View today's bookings" },
    view_confirmed_bookings: { es: 'Ver citas confirmadas', en: 'View confirmed bookings' },
    view_agenda: { es: 'Ver agenda', en: 'View schedule' },
    view_team: { es: 'Ver equipo', en: 'View team' },
    edit_photos: { es: 'Editar fotos del negocio', en: 'Edit business photos' },
    edit_description: { es: 'Editar descripcion del negocio', en: 'Edit business description' },
    edit_schedule: { es: 'Editar horarios de atencion', en: 'Edit business hours' },
    edit_contact: { es: 'Editar contacto y direccion', en: 'Edit contact & address' },
  };

  const PERMISSION_GROUPS = {
    es: {
      'Secciones visibles': ['view_today_bookings', 'view_confirmed_bookings', 'view_agenda', 'view_team'],
      'Acciones en citas': ['complete_bookings', 'reschedule_bookings', 'cancel_bookings'],
      'Clientes': ['block_clients', 'view_client_data'],
      'Perfil del negocio': ['edit_photos', 'edit_description', 'edit_schedule', 'edit_contact'],
      'Negocio': ['edit_services', 'view_reports'],
    },
    en: {
      'Visible sections': ['view_today_bookings', 'view_confirmed_bookings', 'view_agenda', 'view_team'],
      'Booking actions': ['complete_bookings', 'reschedule_bookings', 'cancel_bookings'],
      'Clients': ['block_clients', 'view_client_data'],
      'Business profile': ['edit_photos', 'edit_description', 'edit_schedule', 'edit_contact'],
      'Business': ['edit_services', 'view_reports'],
    },
  };

  const openManagerModal = (worker) => {
    setManagerPermissions(worker.manager_permissions || {
      complete_bookings: true, reschedule_bookings: true, cancel_bookings: false,
      block_clients: false, view_client_data: false, edit_services: false, view_reports: false,
      view_today_bookings: true, view_confirmed_bookings: true, view_agenda: true, view_team: false,
      edit_photos: false, edit_description: false, edit_schedule: false, edit_contact: false,
    });
    setManagerModal({ open: true, worker });
  };

  const handleDesignateManager = async () => {
    try {
      await businessesAPI.designateManager(managerModal.worker.id, managerPermissions);
      toast.success(language === 'es' ? `${managerModal.worker.name} designado como administrador` : `${managerModal.worker.name} designated as administrator`);
      setManagerModal({ open: false, worker: null });
      loadDashboard();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const handleUpdatePermissions = async () => {
    try {
      await businessesAPI.updateManagerPermissions(managerModal.worker.id, managerPermissions);
      toast.success(language === 'es' ? 'Permisos actualizados' : 'Permissions updated');
      setManagerModal({ open: false, worker: null });
      loadDashboard();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const handleRemoveManager = async (worker) => {
    if (!window.confirm(language === 'es' ? `¿Quitar a ${worker.name} como administrador?` : `Remove ${worker.name} as administrator?`)) return;
    try {
      await businessesAPI.removeManager(worker.id);
      toast.success(language === 'es' ? 'Rol de administrador removido' : 'Administrator role removed');
      loadDashboard();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const handleSaveOwnerPin = async () => {
    if (pinValue.length < 4 || pinValue.length > 6 || !/^\d+$/.test(pinValue)) {
      toast.error(language === 'es' ? 'El PIN debe ser de 4-6 dígitos' : 'PIN must be 4-6 digits');
      return;
    }
    if (pinValue !== pinConfirm) {
      toast.error(language === 'es' ? 'Los PINs no coinciden' : 'PINs do not match');
      return;
    }
    try {
      await businessesAPI.setOwnerPin(pinValue);
      toast.success(language === 'es' ? 'PIN de seguridad configurado' : 'Security PIN set');
      setPinModal({ open: false, type: null });
      setPinValue(''); setPinConfirm('');
      setOwnerHasPin(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const handleSetManagerPin = async () => {
    if (pinValue.length < 4 || pinValue.length > 6 || !/^\d+$/.test(pinValue)) {
      toast.error(language === 'es' ? 'El PIN debe ser de 4-6 dígitos' : 'PIN must be 4-6 digits');
      return;
    }
    if (pinValue !== pinConfirm) {
      toast.error(language === 'es' ? 'Los PINs no coinciden' : 'PINs do not match');
      return;
    }
    try {
      await businessesAPI.setManagerPin(pinModal.workerId, pinValue);
      toast.success(language === 'es' ? 'PIN del administrador configurado' : 'Administrator PIN set');
      setPinModal({ open: false, type: null });
      setPinValue(''); setPinConfirm('');
      loadDashboard();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const handleToggleClosure = async (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const isClosed = closedDates.includes(dateStr);

    try {
      if (isClosed) {
        await businessesAPI.removeClosure(dateStr);
        setClosures(prev => prev.filter(c => c.date !== dateStr));
        toast.success(language === 'es' ? 'Día reabierto' : 'Day reopened');
      } else {
        const res = await businessesAPI.addClosure(dateStr, null);
        setClosures(prev => [...prev, res.data]);
        toast.success(language === 'es' ? 'Día marcado como cerrado' : 'Day marked as closed');
      }
    } catch {
      toast.error(language === 'es' ? 'Error' : 'Error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-background">
        <div className="container-app py-8">
          <Skeleton className="h-10 w-64 mb-8" />
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28" />)}
          </div>
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  const biz = dashboardData?.business;
  const stats = dashboardData?.stats;
  // Event venues (salones/banquetes) don't manage staff in Bookvia — the
  // "Equipo" flow is oriented to per-worker calendars (barbers, doctors,
  // stylists, etc.), which doesn't apply here. Placeholders on the services
  // form also adapt to this super-group.
  //
  // Gated by the EVENTS_ENABLED feature flag: while disabled the special
  // treatment is skipped and eventos businesses render the standard UI.
  const isEventsBusiness = isEventsCategoryActive(biz?.super_group);
  // Feed downstream help helpers a "sanitized" super_group so tooltips
  // and banners fall back to the generic copy while Events is disabled.
  const effectiveSuperGroup = isEventsBusiness
    ? biz?.super_group
    : (biz?.super_group === 'eventos' ? null : biz?.super_group);

  return (
    <div className="min-h-screen pt-0 md:pt-20 bg-background" data-testid="business-dashboard-page">
      <div className="container-app py-8">

        {/* ── Phase T: Trial / Subscription CTA banner ─────────────────
             Shows countdown during trial (last 5 days), or blocking CTA
             once trial has expired. Clicking the CTA opens the web-based
             subscription page (bypasses Apple/Google store fees). */}
        {(() => {
          const plan = dashboardData?.plan;
          if (!plan) return null;
          const state = plan.state;
          const days = plan.trial_days_left ?? 0;
          const price = plan.monthly_price_mxn ?? 49.99;

          // Trial active — show countdown only in last 7 days
          if (state === 'trial' && days > 0 && days <= 7) {
            return (
              <div
                className="mb-4 rounded-2xl border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 p-4 flex items-start gap-3"
                data-testid="trial-countdown-banner"
              >
                <Clock className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-amber-900 dark:text-amber-100">
                    {language === 'es'
                      ? `Te quedan ${days} día${days === 1 ? '' : 's'} de tu prueba gratuita`
                      : `${days} day${days === 1 ? '' : 's'} left of your free trial`}
                  </p>
                  <p className="text-xs text-amber-800/80 dark:text-amber-200/80 mt-0.5">
                    {language === 'es'
                      ? `Después: $${price} MXN al mes. Agrega tu tarjeta desde la web para continuar sin interrupciones.`
                      : `After: $${price} MXN/mo. Add your card via the web to continue uninterrupted.`}
                  </p>
                </div>
                <Button
                  size="sm"
                  className="bg-[#F05D5E] hover:bg-[#e04d4e] text-white shrink-0"
                  onClick={() => openExternalBookviaFlow(navigate, '/business/subscription')}
                  data-testid="trial-subscribe-cta"
                >
                  {language === 'es' ? 'Continuar suscripción' : 'Continue'}
                </Button>
              </div>
            );
          }

          // Trial expired — blocking CTA
          if (state === 'expired') {
            return (
              <div
                className="mb-4 rounded-2xl border-2 border-red-400 bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-950/30 dark:to-rose-950/30 p-5"
                data-testid="trial-expired-banner"
              >
                <div className="flex items-start gap-3 mb-3">
                  <AlertTriangle className="h-6 w-6 text-red-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold text-base text-red-900 dark:text-red-100">
                      {language === 'es' ? 'Tu prueba de 30 días terminó' : 'Your 30-day trial has ended'}
                    </p>
                    <p className="text-sm text-red-800/90 dark:text-red-200/90 mt-1 leading-relaxed">
                      {language === 'es'
                        ? `Para seguir recibiendo reservas necesitas agregar tu tarjeta y activar tu suscripción por $${price} MXN al mes. Se cobrará automáticamente cada mes hasta que canceles.`
                        : `To keep receiving bookings, add your card and activate your subscription for $${price} MXN/mo. Charged automatically each month until canceled.`}
                    </p>
                  </div>
                </div>
                <Button
                  className="w-full bg-[#F05D5E] hover:bg-[#e04d4e] text-white h-11"
                  onClick={() => openExternalBookviaFlow(navigate, '/business/subscription')}
                  data-testid="expired-subscribe-cta"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Activar suscripción ($49.99/mes)' : 'Activate subscription ($49.99/mo)'}
                </Button>
                <p className="text-[11px] text-center text-red-700/70 dark:text-red-300/70 mt-2">
                  {language === 'es'
                    ? 'Se abrirá en el navegador web para procesar el pago de forma segura.'
                    : 'Opens in your web browser for secure payment processing.'}
                </p>
              </div>
            );
          }

          return null;
        })()}

        {/* ── Header ─────────────────────────────────── */}
        {isManager && (
          <div className="flex items-center gap-2 p-3 mb-4 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800/40" data-testid="manager-session-banner">
            <UserCog className="h-5 w-5 text-amber-600 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                {language === 'es' ? `Sesión de administrador: ${user?.worker_name || ''}` : `Administrator session: ${user?.worker_name || ''}`}
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300">
                {language === 'es' ? 'Acceso limitado según tus permisos asignados' : 'Limited access based on your assigned permissions'}
              </p>
            </div>
          </div>
        )}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
          <div className="flex items-start gap-4">
            <Avatar className="h-16 w-16 border-2 border-background shadow-lg">
              <AvatarImage src={biz?.logo_url || photos[0]?.url} />
              <AvatarFallback className="bg-[#F05D5E] text-white text-xl font-bold">
                {getInitials(biz?.name || '')}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading font-bold">{biz?.name}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <Badge className={getStatusColor(biz?.status)}>
                  {biz?.status === 'approved' ? (language === 'es' ? 'Aprobado' : 'Approved')
                    : biz?.status === 'pending' ? (language === 'es' ? 'En revisión' : 'Under review')
                    : biz?.status}
                </Badge>
                {biz?.rating > 0 && (
                  <span className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                    {biz?.rating.toFixed(1)} ({biz?.review_count})
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <BranchSelector value={activeBranchId} onChange={setActiveBranchId} language={language} />
            <Button variant="outline" size="sm" onClick={async () => {
              let profileSlug = biz?.slug || biz?.id;
              if (!profileSlug) {
                try {
                  const meRes = await businessesAPI.getDashboard();
                  profileSlug = meRes.data?.business?.slug || meRes.data?.business?.id;
                } catch { /* ignore */ }
              }
              if (profileSlug) {
                navigate(`/business/${profileSlug}`);
              } else {
                toast.error(language === 'es' ? 'Perfil no disponible. Intenta recargar la pagina.' : 'Profile not available. Try reloading the page.');
              }
            }} data-testid="view-profile-button">
              <Eye className="h-4 w-4 mr-1.5" />{language === 'es' ? 'Ver perfil' : 'View profile'}
            </Button>
            <Button size="sm" className="btn-coral" onClick={() => navigate('/business/reception')} data-testid="reception-button">
              <ClipboardList className="h-4 w-4 mr-1.5" />{language === 'es' ? 'Recepcion' : 'Reception'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const slug = biz?.slug || biz?.id;
                if (!slug) {
                  toast.error(language === 'es' ? 'Perfil no disponible aun.' : 'Profile not ready yet.');
                  return;
                }
                const publicUrl = `${window.location.origin}/business/${slug}?ref=share`;
                const businessName = biz?.name || (language === 'es' ? 'mi negocio' : 'my business');
                const message = language === 'es'
                  ? `Hola! 👋 Reserva tu cita en ${businessName} facil y rapido por Bookvia:\n\n${publicUrl}\n\n¡Te espero!`
                  : `Hi! 👋 Book your appointment at ${businessName} fast & easy through Bookvia:\n\n${publicUrl}\n\nLooking forward to seeing you!`;
                const waUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
                window.open(waUrl, '_blank', 'noopener,noreferrer');
              }}
              data-testid="share-business-button"
              title={language === 'es' ? 'Comparte tu negocio por WhatsApp' : 'Share via WhatsApp'}
            >
              <svg className="h-4 w-4 mr-1.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
              </svg>
              {language === 'es' ? 'Compartir' : 'Share'}
            </Button>
            {(hasPermission('edit_description') || hasPermission('edit_schedule') || hasPermission('edit_contact') || hasPermission('edit_photos') || hasPermission('block_clients')) && (
              <Button variant="outline" size="sm" onClick={() => navigate('/business/settings')}>
                <Settings className="h-4 w-4 mr-1.5" />{language === 'es' ? 'Config' : 'Settings'}
              </Button>
            )}
          </div>
        </div>

        {/* Status Alert */}
        {dashboardError && (
          <Card className="mb-6 border-red-500/50 bg-red-50 dark:bg-red-900/20" data-testid="dashboard-error-alert">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-red-800 dark:text-red-200 text-sm">
                  {language === 'es' ? 'Error al cargar el panel' : 'Dashboard loading error'}
                </p>
                <p className="text-xs text-red-700 dark:text-red-300">{dashboardError}</p>
              </div>
              <Button size="sm" variant="outline" onClick={loadDashboard}>
                {language === 'es' ? 'Reintentar' : 'Retry'}
              </Button>
            </CardContent>
          </Card>
        )}
        {biz?.status === 'pending' && (
          <Card className="mb-6 border-yellow-500/50 bg-yellow-50 dark:bg-yellow-900/20">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0" />
              <div>
                <p className="font-medium text-yellow-800 dark:text-yellow-200 text-sm">
                  {language === 'es' ? 'Tu negocio está en revisión' : 'Your business is under review'}
                </p>
                <p className="text-xs text-yellow-700 dark:text-yellow-300">
                  {language === 'es' ? 'No puedes recibir reservas hasta ser aprobado por el administrador.' : 'You cannot receive bookings until approved by admin.'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        {biz?.status === 'needs_revision' && (
          <RevisionRequestBanner biz={biz} language={language} onResubmitted={loadDashboard} />
        )}

        {/* Phase D — Subscription past_due / unpaid banner (intentos fallidos + tarjeta) */}
        <SubscriptionPastDueBanner />

        {/* ── Stats Cards ────────────────────────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            { icon: CalendarIcon, label: language === 'es' ? 'Citas hoy' : "Today's bookings", value: stats?.today_appointments || 0, color: 'text-blue-500 bg-blue-50', type: 'today', title: language === 'es' ? 'Citas de hoy' : "Today's bookings", perm: 'view_today_bookings' },
            { icon: Clock, label: language === 'es' ? 'Confirmadas' : 'Confirmed', value: stats?.pending_appointments || 0, color: 'text-amber-500 bg-amber-50', type: 'pending', title: language === 'es' ? 'Citas confirmadas' : 'Confirmed bookings', perm: 'view_confirmed_bookings' },
            // For deposit-enabled businesses → show platform revenue.
            // For "pay-at-location" businesses → revenue isn't meaningful for them via the platform; show unique customers instead.
            biz?.requires_deposit
              ? { icon: DollarSign, label: language === 'es' ? 'Ingresos mes' : 'Monthly revenue', value: formatCurrency(stats?.month_revenue || 0), color: 'text-emerald-500 bg-emerald-50', type: 'revenue', title: language === 'es' ? 'Ingresos del mes' : 'Monthly revenue', perm: 'view_reports' }
              : { icon: Users, label: language === 'es' ? 'Clientes del mes' : 'Customers this month', value: stats?.unique_customers_month ?? '—', color: 'text-emerald-500 bg-emerald-50', type: 'unique_customers', title: language === 'es' ? 'Clientes unicos del mes' : 'Unique customers this month', perm: 'view_reports' },
            { icon: TrendingUp, label: language === 'es' ? 'Total citas' : 'Total bookings', value: stats?.total_appointments || 0, color: 'text-violet-500 bg-violet-50', type: 'total', title: language === 'es' ? 'Total de citas' : 'Total bookings', perm: 'view_reports' },
          ].filter(stat => hasPermission(stat.perm)).map((stat, i) => (
            <Card 
              key={i} 
              className="border-border/60 cursor-pointer hover:border-[#F05D5E]/30 hover:shadow-sm transition-all group"
              onClick={() => openStatsModal(stat.type, stat.title)}
              data-testid={`stat-card-${stat.type}`}
              title={language === 'es' ? 'Click para ver el detalle' : 'Click to see breakdown'}
            >
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className={`p-2 sm:p-2.5 rounded-xl ${stat.color}`}>
                    <stat.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-[#F05D5E] group-hover:translate-x-0.5 transition-all" />
                </div>
                <p className="text-xl sm:text-2xl font-bold font-heading leading-tight truncate">{stat.value}</p>
                <div className="flex items-center gap-0.5 mt-0.5">
                  <p className="text-[11px] sm:text-xs text-muted-foreground line-clamp-1">{stat.label}</p>
                  {kpiTips[stat.type] ? (
                    <HelpTooltip {...kpiTips[stat.type]} side="bottom" />
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pending no-show reports requiring business response */}
        <NoShowAlertBanner bookings={dayBookings} onResolved={() => loadDayBookings()} />

        {/* Stripe Connect required banner - only relevant if business takes deposits */}
        {biz?.requires_deposit && <StripeConnectRequiredBanner />}

        {/* ── Tabs ────────────────────────────────────── */}
        {(() => {
          const visibleTabs = [
            { value: 'overview', show: hasPermission('view_agenda'), icon: BarChart3, label: language === 'es' ? 'Agenda' : 'Schedule' },
            { value: 'reports', show: hasPermission('view_reports'), icon: TrendingUp, label: language === 'es' ? 'Reportes' : 'Reports' },
            { value: 'clients', show: hasPermission('view_reports') || !isManager, icon: ClipboardList, label: language === 'es' ? 'Clientes' : 'Clients' },
            { value: 'services', show: hasPermission('edit_services'), icon: Briefcase, label: language === 'es' ? 'Servicios' : 'Services' },
            { value: 'team', show: hasPermission('view_team') && !isEventsBusiness, icon: Users, label: language === 'es' ? 'Equipo' : 'Team' },
            // 'branches' tab hidden — multi-branch requires per-branch ownership KYC.
            // Backend endpoints stay live so existing data isn't lost.
            { value: 'branches', show: false, icon: Building2, label: language === 'es' ? 'Sucursales' : 'Branches' },
            { value: 'closures', show: !isManager, icon: CalendarOff, label: language === 'es' ? 'Cierres' : 'Closures' },
            { value: 'photos', show: hasPermission('edit_photos'), icon: Image, label: language === 'es' ? 'Fotos' : 'Photos' },
            { value: 'subscription', show: !isManager, icon: CreditCard, label: language === 'es' ? 'Suscripcion' : 'Subscription' },
            { value: 'activity', show: !isManager, icon: History, label: language === 'es' ? 'Actividad' : 'Activity' },
          ].filter(tab => tab.show);
          return (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Mobile: horizontal scroll list. Desktop (md+): equal-width flex bar. */}
          <TabsList className="flex w-full max-w-3xl overflow-x-auto scrollbar-hide h-auto p-1 gap-1 justify-start md:justify-stretch">
            {visibleTabs.map(tab => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                data-testid={`tab-${tab.value}`}
                className="shrink-0 md:flex-1 md:shrink whitespace-nowrap text-xs sm:text-sm px-3 md:px-2"
              >
                <tab.icon className="h-4 w-4 mr-1.5 hidden sm:inline" />
                {tab.label}
                {tabTips[tab.value] ? (
                  <HelpTooltip {...tabTips[tab.value]} side="bottom" className="ml-1" />
                ) : null}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* ── Overview/Schedule Tab ────────────────── */}
          <TabsContent value="overview" className="mt-6">
            <TabHelpHeader tabKey="overview" language={language} isManager={isManager} superGroup={effectiveSuperGroup} />

            {/* Calendar + Today's bookings — TOP so the business sees them first */}
            <div className="grid lg:grid-cols-3 gap-6 mb-6">
              <Card className="lg:col-span-1">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-heading">{language === 'es' ? 'Calendario' : 'Calendar'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    locale={language === 'es' ? es : enUS}
                    className="rounded-md border"
                  />
                </CardContent>
              </Card>

              <Card className="lg:col-span-2" data-testid="agenda-bookings-card">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-base font-heading">
                    {language === 'es' ? 'Citas del ' : 'Bookings for '}
                    {format(selectedDate, 'PPP', { locale: language === 'es' ? es : enUS })}
                  </CardTitle>
                  <Badge variant="outline">{dayBookings.length}</Badge>
                </CardHeader>
                <CardContent>
                  {/* Leyenda de colores */}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mb-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-blue-500" />{language === 'es' ? 'Confirmada' : 'Confirmed'}</span>
                    <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />{language === 'es' ? 'Completada' : 'Completed'}</span>
                    <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" />{language === 'es' ? 'Pendiente de pago' : 'Pending payment'}</span>
                    <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-red-500" />{language === 'es' ? 'Cancelada' : 'Cancelled'}</span>
                  </div>
                  <AgendaTimeline
                    bookings={dayBookings}
                    language={language}
                    hasPermission={hasPermission}
                    getStatusColor={getStatusColor}
                    t={t}
                    onClientClick={async (booking) => {
                      setBookingDetail(booking);
                      setClientHistory(null);
                      if (booking.user_id) {
                        setHistoryLoading(true);
                        try {
                          const res = await businessesAPI.getClientHistory(booking.user_id);
                          setClientHistory(res.data);
                        } catch { /* ignore */ }
                        setHistoryLoading(false);
                      }
                    }}
                    onComplete={(id) => handleBookingAction(id, 'complete')}
                    onReschedule={openReschedule}
                    onCancel={(id) => handleBookingAction(id, 'cancel')}
                  />
                </CardContent>
              </Card>
            </div>

            <ProfileCompletionBanner
              data={profileCompletion}
              onGoToTab={(target) => {
                if (typeof target === 'string' && target.startsWith('settings:')) {
                  const tabId = target.split(':')[1] || 'info';
                  navigate(`/business/settings?tab=${encodeURIComponent(tabId)}`);
                } else {
                  setActiveTab(target);
                }
              }}
            />

            {/* Dashboard Summary Cards */}
            {dashSummary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="biz-dashboard-summary">
                <Card>
                  <CardContent className="p-3.5">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="p-1.5 rounded-lg bg-blue-50"><CalendarIcon className="h-4 w-4 text-blue-600" /></div>
                      <span className="text-xs text-muted-foreground">{t('Hoy', 'Today')}</span>
                    </div>
                    <p className="text-xl font-bold">{dashSummary.today?.bookings || 0}</p>
                    <p className="text-xs text-muted-foreground">{t('citas', 'bookings')} · {formatCurrency(dashSummary.today?.revenue || 0)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3.5">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="p-1.5 rounded-lg bg-emerald-50"><DollarSign className="h-4 w-4 text-emerald-600" /></div>
                      <span className="text-xs text-muted-foreground">{t('Esta semana', 'This week')}</span>
                    </div>
                    <p className="text-xl font-bold">{formatCurrency(dashSummary.week?.revenue || 0)}</p>
                    <p className="text-xs text-muted-foreground">
                      {dashSummary.week?.bookings || 0} {t('citas', 'bookings')}
                      {dashSummary.week?.change_pct !== 0 && (
                        <span className={dashSummary.week?.change_pct > 0 ? 'text-emerald-600 ml-1' : 'text-red-500 ml-1'}>
                          {dashSummary.week?.change_pct > 0 ? '+' : ''}{dashSummary.week?.change_pct}%
                        </span>
                      )}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3.5">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="p-1.5 rounded-lg bg-purple-50"><Users className="h-4 w-4 text-purple-600" /></div>
                      <span className="text-xs text-muted-foreground">{t('Este mes', 'This month')}</span>
                    </div>
                    <p className="text-xl font-bold">{dashSummary.month?.unique_clients || 0}</p>
                    <p className="text-xs text-muted-foreground">{t('clientes unicos', 'unique clients')} · {dashSummary.month?.bookings || 0} {t('citas', 'bookings')}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3.5">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="p-1.5 rounded-lg bg-amber-50"><Star className="h-4 w-4 text-amber-600" /></div>
                      <span className="text-xs text-muted-foreground">{t('Resenas nuevas', 'New reviews')}</span>
                    </div>
                    <p className="text-xl font-bold">{dashSummary.new_reviews || 0}</p>
                    <p className="text-xs text-muted-foreground">{t('ultimos 7 dias', 'last 7 days')}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Phase 14: Acquisition metrics (views → conversion → top services) */}
            {dashSummary && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="biz-acquisition-metrics">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="p-1.5 rounded-lg bg-sky-50"><Eye className="h-4 w-4 text-sky-600" /></div>
                      <span className="text-xs text-muted-foreground">{t('Vistas a tu perfil', 'Profile views')}</span>
                    </div>
                    <p className="text-2xl font-heading font-bold">{dashSummary.profile_views_30d || 0}</p>
                    <p className="text-xs text-muted-foreground">{t('ultimos 30 dias', 'last 30 days')}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="p-1.5 rounded-lg bg-emerald-50"><TrendingUp className="h-4 w-4 text-emerald-600" /></div>
                      <span className="text-xs text-muted-foreground">{t('Conversion a reserva', 'View-to-booking')}</span>
                    </div>
                    <p className="text-2xl font-heading font-bold">
                      {dashSummary.profile_views_30d > 0 ? `${dashSummary.conversion_pct}%` : '—'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {dashSummary.bookings_30d || 0} / {dashSummary.profile_views_30d || 0} {t('vistas', 'views')}
                    </p>
                  </CardContent>
                </Card>
                <Card data-testid="top-services-card">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 rounded-lg bg-rose-50"><BarChart3 className="h-4 w-4 text-rose-600" /></div>
                      <span className="text-xs text-muted-foreground">{t('Top servicios (30d)', 'Top services (30d)')}</span>
                    </div>
                    {dashSummary.top_services && dashSummary.top_services.length > 0 ? (
                      <ol className="space-y-1.5">
                        {dashSummary.top_services.map((s, i) => (
                          <li key={s.service_id} className="flex items-center justify-between text-xs gap-2" data-testid={`top-service-${i}`}>
                            <span className="min-w-0 flex items-center gap-2">
                              <span className="font-bold text-slate-500">{i + 1}.</span>
                              <span className="truncate font-medium">{s.name}</span>
                            </span>
                            <span className="text-muted-foreground shrink-0 tabular-nums">
                              {s.bookings} {t('res.', 'bk.')}
                            </span>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <p className="text-xs text-muted-foreground italic py-2">
                        {t('Sin datos aun. Las reservas aparecen aqui cuando tengas actividad.', 'No data yet. Bookings show up here once you have activity.')}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

          </TabsContent>

          {/* ── Reports Tab ─────────────────────────── */}
          <TabsContent value="reports" className="mt-6">
            <TabHelpHeader tabKey="reports" language={language} isManager={isManager} superGroup={effectiveSuperGroup} />
            <ReportsTab language={language} />
          </TabsContent>

          {/* ── Clients (mini CRM) Tab ──────────────── */}
          <TabsContent value="clients" className="mt-6">
            <TabHelpHeader tabKey="clients" language={language} isManager={isManager} superGroup={effectiveSuperGroup} />
            <BusinessClientsTab />
          </TabsContent>

          {/* ── Services Tab ─────────────────────────── */}
          <TabsContent value="services" className="mt-6">
            <TabHelpHeader tabKey="services" language={language} isManager={isManager} superGroup={effectiveSuperGroup} />
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-base font-heading">{language === 'es' ? 'Mis servicios' : 'My services'}</CardTitle>
                <Button size="sm" className="btn-coral" onClick={() => navigate('/business/services')} data-testid="manage-services-btn">
                  <Plus className="h-4 w-4 mr-1" />{language === 'es' ? 'Gestionar' : 'Manage'}
                </Button>
              </CardHeader>
              <CardContent>
                {services.length > 0 ? (
                  <div className="space-y-3">
                    {services.map(service => (
                      <div key={service.id} className="flex items-center justify-between p-3 rounded-xl border border-border/60" data-testid={`service-item-${service.id}`}>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{service.name}</p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{service.duration_minutes} min</span>
                            {service.is_home_service && <Badge variant="outline" className="text-[10px] h-4">Domicilio</Badge>}
                          </div>
                        </div>
                        <span className="text-base font-bold text-[#F05D5E]">{formatCurrency(service.price)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Briefcase className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground mb-3">{language === 'es' ? 'No tienes servicios' : 'No services yet'}</p>
                    <Button size="sm" className="btn-coral" onClick={() => navigate('/business/services')}>
                      <Plus className="h-4 w-4 mr-1" />{language === 'es' ? 'Agregar servicio' : 'Add service'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Team Tab ─────────────────────────────── */}
          <TabsContent value="team" className="mt-6 space-y-6">
            <TabHelpHeader tabKey="team" language={language} isManager={isManager} superGroup={effectiveSuperGroup} />
            {/* Security PIN Section */}
            <Card className="border-amber-200/60 dark:border-amber-800/40">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Shield className="h-4 w-4 text-amber-500" />
                  {language === 'es' ? 'PIN de seguridad del dueño' : 'Owner security PIN'}
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  {language === 'es'
                    ? 'Configura un PIN numérico para proteger acciones sensibles. Los administradores usarán su propio PIN.'
                    : 'Set a numeric PIN to protect sensitive actions. Administrators will use their own PIN.'}
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className={`shrink-0 p-2 rounded-lg self-start ${ownerHasPin ? 'bg-green-50 dark:bg-green-900/20' : 'bg-amber-50 dark:bg-amber-900/20'}`}>
                    <Shield className={`h-5 w-5 ${ownerHasPin ? 'text-green-600' : 'text-amber-500'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">
                      {ownerHasPin
                        ? (language === 'es' ? 'PIN configurado' : 'PIN configured')
                        : (language === 'es' ? 'Sin PIN configurado' : 'No PIN configured')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {ownerHasPin
                        ? (language === 'es' ? 'Tu PIN está activo y protege acciones sensibles' : 'Your PIN is active and protects sensitive actions')
                        : (language === 'es' ? 'Configura un PIN para mayor seguridad' : 'Set a PIN for extra security')}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant={ownerHasPin ? 'outline' : 'default'}
                    className={`w-full sm:w-auto shrink-0 ${!ownerHasPin ? 'btn-coral' : ''}`}
                    onClick={() => { setPinModal({ open: true, type: 'owner_setup' }); setPinValue(''); setPinConfirm(''); }}
                    data-testid="setup-owner-pin-btn"
                  >
                    <Shield className="h-3.5 w-3.5 mr-1.5" />
                    {ownerHasPin
                      ? (language === 'es' ? 'Cambiar PIN' : 'Change PIN')
                      : (language === 'es' ? 'Configurar PIN' : 'Set PIN')}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Team Members Card */}
            <Card>
              <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between pb-3 gap-3">
                <div className="min-w-0">
                  <CardTitle className="text-base font-heading">{language === 'es' ? 'Mi equipo' : 'My team'}</CardTitle>
                  <AdminRoleInfoModal triggerClassName="mt-1 -ml-2" />
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:shrink-0 w-full sm:w-auto">
                  {business?.public_code && (
                    <ShareAdminLinkButton
                      publicCode={business.public_code}
                      businessName={business.name}
                      className="w-full sm:w-auto justify-center"
                    />
                  )}
                  <Button size="sm" className="btn-coral w-full sm:w-auto shrink-0" onClick={() => navigate('/business/team')} data-testid="manage-team-btn">
                    <Plus className="h-4 w-4 mr-1" />{language === 'es' ? 'Gestionar' : 'Manage'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {workers.length > 0 ? (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {workers.map(worker => (
                      <div key={worker.id} className="p-3 rounded-xl border border-border/60 space-y-3" data-testid={`worker-item-${worker.id}`}>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-11 w-11">
                            <AvatarImage src={worker.photo_url} />
                            <AvatarFallback className="bg-[#F05D5E]/10 text-[#F05D5E] text-sm font-bold">
                              {getInitials(worker.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="font-medium text-sm truncate">{worker.name}</p>
                              {worker.is_manager && (
                                <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] shrink-0" data-testid={`manager-badge-${worker.id}`}>
                                  <UserCog className="h-3 w-3 mr-0.5" />
                                  {language === 'es' ? 'Administrador' : 'Administrator'}
                                </Badge>
                              )}
                            </div>
                            {worker.bio && <p className="text-xs text-muted-foreground truncate">{worker.bio}</p>}
                            <p className="text-xs text-muted-foreground">{worker.service_ids?.length || 0} {language === 'es' ? 'servicios' : 'services'}</p>
                          </div>
                          <Badge variant={worker.active !== false ? 'default' : 'secondary'} className="text-[10px] shrink-0">
                            {worker.active !== false ? (language === 'es' ? 'Activo' : 'Active') : (language === 'es' ? 'Inactivo' : 'Inactive')}
                          </Badge>
                        </div>
                        {/* Manager Actions */}
                        <div className="flex items-center gap-1.5 pt-1 border-t border-border/40">
                          {worker.is_manager ? (
                            <>
                              <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => openManagerModal(worker)} data-testid={`edit-permissions-${worker.id}`}>
                                <Settings className="h-3 w-3 mr-1" />
                                {language === 'es' ? 'Permisos' : 'Permissions'}
                              </Button>
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setPinModal({ open: true, type: 'manager_pin', workerId: worker.id, workerName: worker.name }); setPinValue(''); setPinConfirm(''); }} data-testid={`set-pin-${worker.id}`}>
                                <Shield className="h-3 w-3 mr-1" />
                                {worker.has_manager_pin ? (language === 'es' ? 'Cambiar PIN' : 'Change PIN') : 'PIN'}
                              </Button>
                              <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleRemoveManager(worker)} data-testid={`remove-manager-${worker.id}`}>
                                <XCircle className="h-3 w-3 mr-1" />
                                {language === 'es' ? 'Quitar' : 'Remove'}
                              </Button>
                            </>
                          ) : (
                            <Button size="sm" variant="outline" className="h-7 text-xs w-full text-amber-700 border-amber-200 hover:bg-amber-50" onClick={() => openManagerModal(worker)} data-testid={`designate-manager-${worker.id}`}>
                              <UserCog className="h-3 w-3 mr-1" />
                              {language === 'es' ? 'Designar como administrador' : 'Designate as administrator'}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Users className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground mb-3">{language === 'es' ? 'No tienes miembros en el equipo' : 'No team members yet'}</p>
                    <Button size="sm" className="btn-coral" onClick={() => navigate('/business/team')}>
                      <Plus className="h-4 w-4 mr-1" />{language === 'es' ? 'Agregar profesional' : 'Add professional'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Branches (Sucursales) Tab ────────────── */}
          <TabsContent value="branches" className="mt-6">
            <BranchesTab language={language} />
          </TabsContent>

          {/* ── Closures Tab ────────────────────────── */}
          <TabsContent value="closures" className="mt-6">
            <TabHelpHeader tabKey="closures" language={language} isManager={isManager} superGroup={effectiveSuperGroup} />
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-heading flex items-center gap-2">
                    <CalendarOff className="h-4 w-4 text-[#F05D5E]" />
                    {language === 'es' ? 'Marcar días cerrados' : 'Mark closed days'}
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">
                    {language === 'es'
                      ? 'Haz clic en un día para marcarlo como cerrado. Los clientes no podrán reservar en esos días.'
                      : 'Click a day to mark it as closed. Customers won\'t be able to book on those days.'}
                  </p>
                </CardHeader>
                <CardContent>
                  <Calendar
                    mode="single"
                    onSelect={(date) => date && handleToggleClosure(date)}
                    disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                    locale={language === 'es' ? es : enUS}
                    modifiers={{ closed: closedDates.map(d => new Date(d + 'T12:00:00')) }}
                    modifiersClassNames={{ closed: 'bg-red-100 text-red-700 font-bold dark:bg-red-900/40 dark:text-red-400' }}
                    className="rounded-md border"
                    data-testid="closures-calendar"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-base font-heading">
                    {language === 'es' ? 'Días cerrados programados' : 'Scheduled closures'}
                  </CardTitle>
                  <Badge variant="outline">{closures.length}</Badge>
                </CardHeader>
                <CardContent>
                  {closures.length > 0 ? (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                      {closures
                        .sort((a, b) => a.date.localeCompare(b.date))
                        .map(closure => {
                          const dateObj = new Date(closure.date + 'T12:00:00');
                          const isPast = dateObj < new Date(new Date().setHours(0,0,0,0));
                          return (
                            <div
                              key={closure.date}
                              className={`flex items-center justify-between p-3 rounded-xl border ${isPast ? 'opacity-50' : 'border-red-200 dark:border-red-900/40 bg-red-50/50 dark:bg-red-900/10'}`}
                              data-testid={`closure-${closure.date}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex flex-col items-center justify-center shrink-0">
                                  <span className="text-[10px] text-red-600 dark:text-red-400 uppercase font-medium">
                                    {dateObj.toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { month: 'short' })}
                                  </span>
                                  <span className="text-base font-bold text-red-700 dark:text-red-300">{dateObj.getDate()}</span>
                                </div>
                                <div>
                                  <p className="text-sm font-medium capitalize">
                                    {dateObj.toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { weekday: 'long' })}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {dateObj.toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                  </p>
                                  {closure.reason && <p className="text-xs text-muted-foreground mt-0.5">{closure.reason}</p>}
                                </div>
                              </div>
                              {!isPast && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20"
                                  onClick={() => handleToggleClosure(dateObj)}
                                  data-testid={`remove-closure-${closure.date}`}
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <CalendarOff className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
                      <p className="text-sm text-muted-foreground mb-1">
                        {language === 'es' ? 'No tienes días cerrados' : 'No closed days scheduled'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {language === 'es' ? 'Selecciona días en el calendario para cerrar' : 'Select days in the calendar to close'}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ── Photos Tab ───────────────────────────── */}
          <TabsContent value="photos" className="mt-6">
            <TabHelpHeader tabKey="photos" language={language} isManager={isManager} superGroup={effectiveSuperGroup} />
            {/* Section 1 + 2: Logo and Cover branding editors */}
            <BusinessBrandingSection
              business={biz}
              t={(es, en) => language === 'es' ? es : en}
              language={language}
              onChange={loadDashboard}
            />

            {/* Section 3: General gallery */}
            <Card data-testid="photos-gallery-card">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div>
                  <CardTitle className="text-base font-heading">{language === 'es' ? 'Galería del lugar' : 'Gallery'}</CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">{language === 'es' ? 'Fotos generales del lugar, equipo y trabajos. Aparecen en tu perfil publico.' : 'General photos of the place, team and work. Shown on your public profile.'}</p>
                </div>
                <label className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg cursor-pointer transition-colors ${uploading || photos.length >= 10 ? 'bg-muted text-muted-foreground cursor-not-allowed' : 'btn-coral text-white'}`}>
                  <Upload className="h-4 w-4" />
                  {uploading ? (language === 'es' ? 'Subiendo...' : 'Uploading...') : photos.length >= 10 ? (language === 'es' ? 'Limite alcanzado' : 'Limit reached') : (language === 'es' ? `Subir fotos (${photos.length}/10)` : `Upload photos (${photos.length}/10)`)}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,.jfif"
                    multiple
                    onChange={handlePhotoUpload}
                    disabled={uploading || photos.length >= 10}
                    className="hidden"
                    data-testid="photo-upload-input"
                  />
                </label>
              </CardHeader>
              <CardContent>
                {photos.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {photos.map(photo => (
                      <div key={photo.id} className="relative group aspect-square rounded-xl overflow-hidden border" data-testid={`photo-${photo.id}`}>
                        <img src={photo.url} alt={photo.original_filename} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <Button size="icon" variant="destructive" className="h-8 w-8" onClick={() => handleDeletePhoto(photo.id)} data-testid={`delete-photo-${photo.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 border-2 border-dashed rounded-xl">
                    <Image className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground mb-1">{language === 'es' ? 'No tienes fotos aún' : 'No photos yet'}</p>
                    <p className="text-xs text-muted-foreground mb-4">{language === 'es' ? 'Sube fotos de tu negocio, servicios y equipo' : 'Upload photos of your business, services and team'}</p>
                    <label className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg cursor-pointer btn-coral text-white">
                      <Upload className="h-4 w-4" />
                      {language === 'es' ? 'Subir primera foto' : 'Upload first photo'}
                      <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple onChange={handlePhotoUpload} className="hidden" />
                    </label>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Subscription Tab ──────────────────────── */}
          <TabsContent value="subscription" className="mt-6">
            <TabHelpHeader tabKey="subscription" language={language} isManager={isManager} superGroup={effectiveSuperGroup} />
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-[#F05D5E]" />
                  {language === 'es' ? 'Mi suscripcion' : 'My subscription'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Subscription status */}
                <div className="rounded-xl border p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">{language === 'es' ? 'Estado' : 'Status'}</p>
                    <Badge data-testid="subscription-status-badge" className={
                      subscriptionData?.subscription_status === 'active' ? 'bg-green-100 text-green-800 border-green-200' :
                      subscriptionData?.subscription_status === 'trialing' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                      subscriptionData?.subscription_status === 'past_due' ? 'bg-red-100 text-red-800 border-red-200' :
                      subscriptionData?.subscription_status === 'canceled' ? 'bg-gray-100 text-gray-800 border-gray-200' :
                      'bg-yellow-100 text-yellow-800 border-yellow-200'
                    }>
                      {subscriptionData?.subscription_status === 'active' ? (language === 'es' ? 'Activa' : 'Active') :
                       subscriptionData?.subscription_status === 'trialing' ? (language === 'es' ? 'Periodo de prueba' : 'Trial') :
                       subscriptionData?.subscription_status === 'past_due' ? (language === 'es' ? 'Pago vencido' : 'Past due') :
                       subscriptionData?.subscription_status === 'canceled' ? (language === 'es' ? 'Cancelada' : 'Canceled') :
                       (language === 'es' ? 'Sin suscripcion' : 'No subscription')}
                    </Badge>
                  </div>

                  {subscriptionData?.subscription_status === 'trialing' && (
                    <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <Shield className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        {subscriptionData?.country_code === 'US'
                          ? (language === 'es'
                            ? 'Estas en tu periodo de prueba gratuito de 30 dias. El primer cobro de $4.99 USD se realizara automaticamente al terminar el periodo.'
                            : 'You are in your 30-day free trial. The first charge of $4.99 USD will be made automatically at the end of the trial.')
                          : (language === 'es'
                            ? 'Estas en tu periodo de prueba gratuito de 30 dias. El primer cobro de $49.99 MXN se realizara automaticamente al terminar el periodo.'
                            : 'You are in your 30-day free trial. The first charge of $49.99 MXN will be made automatically at the end of the trial.')}
                      </p>
                    </div>
                  )}

                  {subscriptionData?.current_period_end && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{language === 'es' ? 'Proximo cobro' : 'Next charge'}</span>
                      <span className="font-medium">{new Date(subscriptionData.current_period_end).toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{language === 'es' ? 'Precio' : 'Price'}</span>
                    <span className="font-medium">{subscriptionData?.country_code === 'US' ? '$4.99 USD' : '$49.99 MXN'} / {language === 'es' ? 'mes' : 'month'}</span>
                  </div>

                  {subscriptionData?.cancel_at_period_end && (
                    <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        {language === 'es'
                          ? 'Tu suscripcion esta programada para cancelarse al final del periodo actual. Despues de eso, tu negocio dejara de ser visible en la plataforma.'
                          : 'Your subscription is scheduled to cancel at the end of the current period. After that, your business will stop being visible on the platform.'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Cancel button */}
                {subscriptionData?.subscription_id && !subscriptionData?.cancel_at_period_end && (
                  <div className="pt-2 border-t">
                    <Button
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                      onClick={handleCancelSubscription}
                      disabled={cancelingSubscription}
                      data-testid="cancel-subscription-button"
                    >
                      {cancelingSubscription
                        ? (language === 'es' ? 'Cancelando...' : 'Canceling...')
                        : (language === 'es' ? 'Cancelar suscripcion' : 'Cancel subscription')}
                    </Button>
                    <p className="text-[11px] text-muted-foreground mt-2">
                      {language === 'es'
                        ? 'Si cancelas, tu negocio dejara de aparecer en la plataforma al terminar el periodo actual.'
                        : 'If you cancel, your business will stop appearing on the platform at the end of the current period.'}
                    </p>
                  </div>
                )}

                {/* No subscription - show subscribe button */}
                {(!subscriptionData?.subscription_id) && (
                  <div className="text-center py-8 border-2 border-dashed rounded-xl">
                    <CreditCard className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground mb-4">{language === 'es' ? 'No tienes una suscripcion activa' : 'You have no active subscription'}</p>
                    <Button className="btn-coral" onClick={async () => {
                      // ── Apple/Google commission avoidance ──
                      // On the native app we send the merchant to the web to
                      // subscribe. Stripe Checkout never runs inside the app,
                      // so the store does NOT take a 15-30% cut.
                      if (isNativeApp()) {
                        await openExternalUrl(`${PUBLIC_WEB_URL}/business/dashboard?tab=subscription&from=app`);
                        return;
                      }
                      try {
                        const res = await businessesAPI.createSubscription(window.location.origin);
                        if (res.data?.url) window.location.href = res.data.url;
                      } catch { toast.error(language === 'es' ? 'Error al iniciar suscripcion' : 'Error starting subscription'); }
                    }} data-testid="activate-subscription-button">
                      <CreditCard className="h-4 w-4 mr-2" />
                      {language === 'es' ? 'Activar suscripcion' : 'Activate subscription'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Activity Log Tab ────────────────────────── */}
          <TabsContent value="activity" className="mt-6">
            <TabHelpHeader tabKey="activity" language={language} isManager={isManager} superGroup={effectiveSuperGroup} />
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <History className="h-4 w-4 text-muted-foreground" />
                  {language === 'es' ? 'Historial de actividad' : 'Activity log'}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <select
                    className="text-xs border rounded-md px-2 py-1.5 bg-background"
                    value={activityFilter}
                    onChange={(e) => { setActivityFilter(e.target.value); loadActivityLogs(1, e.target.value); }}
                    data-testid="activity-filter"
                  >
                    <option value="all">{language === 'es' ? 'Todos' : 'All'}</option>
                    <option value="owner">{language === 'es' ? 'Dueño' : 'Owner'}</option>
                    <option value="admin">{language === 'es' ? 'Administradores' : 'Administrators'}</option>
                  </select>
                  <Button size="sm" variant="outline" onClick={() => loadActivityLogs(1, activityFilter)} data-testid="refresh-activity-btn">
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {activityLoading ? (
                  <div className="space-y-3">
                    {[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}
                  </div>
                ) : activityLogs.length > 0 ? (
                  <>
                    <div className="space-y-2">
                      {activityLogs.map(log => {
                        const actionLabels = {
                          complete_booking: { es: 'Completó cita', en: 'Completed booking', icon: CheckCircle2, color: 'text-green-600 bg-green-50' },
                          cancel_booking: { es: 'Canceló cita', en: 'Cancelled booking', icon: XCircle, color: 'text-red-600 bg-red-50' },
                          reschedule_booking: { es: 'Reagendó cita', en: 'Rescheduled booking', icon: RefreshCw, color: 'text-blue-600 bg-blue-50' },
                          designate_admin: { es: 'Designó administrador', en: 'Designated administrator', icon: UserCog, color: 'text-amber-600 bg-amber-50' },
                          remove_admin: { es: 'Removió administrador', en: 'Removed administrator', icon: XCircle, color: 'text-orange-600 bg-orange-50' },
                          update_permissions: { es: 'Actualizó permisos', en: 'Updated permissions', icon: Shield, color: 'text-violet-600 bg-violet-50' },
                        };
                        const meta = actionLabels[log.action] || { es: log.action, en: log.action, icon: History, color: 'text-gray-600 bg-gray-50' };
                        const IconComp = meta.icon;
                        const dt = new Date(log.created_at);
                        const timeStr = dt.toLocaleTimeString(language === 'es' ? 'es-MX' : 'en-US', { hour: '2-digit', minute: '2-digit' });
                        const dateStr = dt.toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { day: 'numeric', month: 'short' });

                        return (
                          <div key={log.id} className="flex items-start gap-3 p-3 rounded-lg border border-border/50 hover:bg-muted/20 transition-colors" data-testid={`activity-log-${log.id}`}>
                            <div className={`p-2 rounded-lg shrink-0 ${meta.color}`}>
                              <IconComp className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium">{meta[language] || meta.es}</span>
                                <Badge variant={log.actor_type === 'admin' ? 'outline' : 'secondary'} className="text-[10px]">
                                  {log.actor_type === 'admin' ? (
                                    <><UserCog className="h-2.5 w-2.5 mr-0.5" />{log.actor_name}</>
                                  ) : (
                                    <><Shield className="h-2.5 w-2.5 mr-0.5" />{language === 'es' ? 'Dueño' : 'Owner'}</>
                                  )}
                                </Badge>
                              </div>
                              {/* Details line */}
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {log.details?.client_name && `${log.details.client_name} — `}
                                {log.details?.service_name && `${log.details.service_name} `}
                                {log.details?.date && `(${new Date(log.details.date + 'T12:00:00').toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { day: 'numeric', month: 'short' })})`}
                                {log.details?.worker_name && !log.details?.client_name && log.details.worker_name}
                                {log.action === 'reschedule_booking' && log.details?.old_date && (
                                  <> → {log.details.new_date} {log.details.new_time}</>
                                )}
                                {log.details?.reason && ` — ${log.details.reason}`}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-xs text-muted-foreground">{dateStr}</p>
                              <p className="text-[10px] text-muted-foreground/70">{timeStr}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Pagination */}
                    {activityPages > 1 && (
                      <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t">
                        <Button size="sm" variant="outline" disabled={activityPage <= 1} onClick={() => loadActivityLogs(activityPage - 1, activityFilter)} data-testid="activity-prev-page">
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-xs text-muted-foreground">{activityPage} / {activityPages}</span>
                        <Button size="sm" variant="outline" disabled={activityPage >= activityPages} onClick={() => loadActivityLogs(activityPage + 1, activityFilter)} data-testid="activity-next-page">
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12">
                    <History className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">{language === 'es' ? 'No hay actividad registrada aún' : 'No activity recorded yet'}</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">
                      {language === 'es' ? 'Las acciones de dueños y administradores aparecerán aquí' : 'Owner and administrator actions will appear here'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
          );
        })()}

        {/* ── Stats Detail Modal ─────────────────────── */}
        <Dialog open={statsModal.open} onOpenChange={(open) => { if (!open) closeStatsModal(); }}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="stats-detail-modal">
            <DialogHeader>
              <DialogTitle className="font-heading">{statsModal.title}</DialogTitle>
              <DialogDescription>
                {statsModal.type === 'revenue' 
                  ? (language === 'es' ? 'Citas completadas con ingresos' : 'Completed bookings with revenue')
                  : (language === 'es' ? 'Lista de citas' : 'Bookings list')}
              </DialogDescription>
            </DialogHeader>

            {/* Date Range Filter (for total and revenue) */}
            {(statsModal.type === 'total' || statsModal.type === 'revenue') && (
              <div className="flex items-end gap-2 pb-2 border-b">
                <div className="flex-1">
                  <Label className="text-xs">{language === 'es' ? 'Desde' : 'From'}</Label>
                  <Input type="date" value={statsDateFrom} onChange={e => setStatsDateFrom(e.target.value)} data-testid="stats-date-from" />
                </div>
                <div className="flex-1">
                  <Label className="text-xs">{language === 'es' ? 'Hasta' : 'To'}</Label>
                  <Input type="date" value={statsDateTo} onChange={e => setStatsDateTo(e.target.value)} data-testid="stats-date-to" />
                </div>
                <Button size="sm" className="btn-coral shrink-0" onClick={() => openStatsModal(statsModal.type, statsModal.title, statsDateFrom, statsDateTo)} data-testid="stats-filter-btn">
                  {language === 'es' ? 'Filtrar' : 'Filter'}
                </Button>
              </div>
            )}

            {/* Results */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
              {statsModal.loading ? (
                <div className="space-y-3 py-4">
                  {[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}
                </div>
              ) : statsModal.bookings.length > 0 ? (
                <>
                  {statsModal.totalRevenue !== null && biz?.requires_deposit && (
                    <div className="flex items-center justify-between p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200">
                      <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                        {language === 'es' ? 'Ingresos totales' : 'Total revenue'}
                      </span>
                      <span className="text-lg font-bold text-emerald-700 dark:text-emerald-300" data-testid="stats-total-revenue">
                        {formatCurrency(statsModal.totalRevenue)}
                      </span>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">{statsModal.bookings.length} {language === 'es' ? 'citas' : 'bookings'}</p>
                  {statsModal.bookings.map(b => (
                    <div key={b.id} className="flex items-center gap-3 p-3 rounded-xl border border-border/60" data-testid={`stats-booking-${b.id}`}>
                      <div className="w-14 text-center shrink-0">
                        <p className="text-[10px] uppercase text-muted-foreground font-medium">
                          {new Date(b.date + 'T12:00:00').toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { month: 'short' })}
                        </p>
                        <p className="text-lg font-bold leading-none">{new Date(b.date + 'T12:00:00').getDate()}</p>
                      </div>
                      <Separator orientation="vertical" className="h-10" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{b.client_name || b.user_name || (language === 'es' ? 'Cliente' : 'Client')}</p>
                        <p className="text-xs text-muted-foreground truncate">{b.service_name}</p>
                        <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-0.5">
                          <span>{formatTime(b.time)} - {formatTime(b.end_time)}</span>
                          {b.worker_name && <span>| {b.worker_name}</span>}
                        </div>
                        {(b.booked_by === 'business' || (b.skip_payment && b.client_name)) && (
                          <div className="flex gap-1 mt-1">
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">
                              {language === 'es' ? 'Recepcion' : 'Reception'}
                            </span>
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
                              {language === 'es' ? 'Pago en negocio' : 'Pay at business'}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <Badge className={`text-[10px] ${getStatusColor(b.status)}`}>
                          {t(`status.${b.status}`)}
                        </Badge>
                        {b.total_amount && (
                          <span className="text-xs font-medium text-[#F05D5E]">{formatCurrency(b.total_amount)}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="text-center py-12">
                  <CalendarIcon className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">{language === 'es' ? 'No hay citas' : 'No bookings'}</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Business Cancellation Confirmation Modal — Step 1: confirm, Step 2: reason */}
        <Dialog
          open={cancelDialog.open}
          onOpenChange={(open) => !open && !cancelDialog.submitting && setCancelDialog({ open: false, booking: null, step: 'confirm', reason: '', submitting: false, preview: null, previewLoading: false })}
        >
          <DialogContent className="max-w-md" data-testid="business-cancel-modal">
            {cancelDialog.step === 'confirm' && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                    {language === 'es' ? '¿Cancelar esta cita?' : 'Cancel this booking?'}
                  </DialogTitle>
                  <DialogDescription>
                    {cancelDialog.booking && (
                      <span className="block text-foreground/80 text-sm leading-relaxed">
                        <strong>{cancelDialog.booking.user_name || cancelDialog.booking.client_name}</strong>
                        {' — '}
                        {cancelDialog.booking.service_name}
                        <br />
                        <span className="text-muted-foreground">
                          {cancelDialog.booking.date} {cancelDialog.booking.time}
                        </span>
                      </span>
                    )}
                  </DialogDescription>
                </DialogHeader>
                {cancelDialog.previewLoading ? (
                  <div className="rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground text-center">
                    {language === 'es' ? 'Calculando impacto economico...' : 'Calculating economic impact...'}
                  </div>
                ) : cancelDialog.preview?.summary ? (
                  <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800 p-3.5 space-y-2" data-testid="cancel-preview-business">
                    <div className="flex items-start gap-2">
                      <DollarSign className="h-4 w-4 text-amber-700 dark:text-amber-300 shrink-0 mt-0.5" />
                      <p className="text-sm font-bold text-amber-900 dark:text-amber-100 leading-snug">
                        {language === 'es' ? cancelDialog.preview.summary.title_es : cancelDialog.preview.summary.title_en}
                      </p>
                    </div>
                    <ul className="space-y-1 text-xs text-amber-800 dark:text-amber-200 leading-relaxed pl-6 list-disc">
                      {(language === 'es' ? cancelDialog.preview.summary.lines_es : cancelDialog.preview.summary.lines_en).map((line, i) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
                    {language === 'es'
                      ? 'Al cancelar, el cliente recibirá un reembolso completo y se le notificará por correo. Las cancelaciones frecuentes pueden afectar tu reputación.'
                      : 'When you cancel, the client receives a full refund and an email notification. Frequent cancellations can hurt your reputation.'}
                  </div>
                )}
                {/* Reschedule recommendation: ask the client to reschedule instead */}
                <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-800 p-3.5" data-testid="cancel-reschedule-recommendation">
                  <div className="flex items-start gap-2">
                    <RefreshCw className="h-4 w-4 text-blue-700 dark:text-blue-300 shrink-0 mt-0.5" />
                    <div className="space-y-1.5">
                      <p className="text-sm font-bold text-blue-900 dark:text-blue-100 leading-snug">
                        {language === 'es' ? '¿Ya hablaste con el cliente?' : 'Have you contacted the client?'}
                      </p>
                      <p className="text-xs text-blue-800 dark:text-blue-200 leading-relaxed">
                        {language === 'es'
                          ? 'Antes de cancelar, te recomendamos contactar al cliente y proponerle reagendar. Solo el cliente puede reagendar la cita, asi que avisale para que entre a Bookvia y elija nueva fecha. Esto evita la penalty y conserva la confianza.'
                          : 'Before cancelling, we recommend contacting the client to suggest a reschedule. Only the client can reschedule, so let them know to enter Bookvia and pick a new date. This avoids the penalty and preserves trust.'}
                      </p>
                    </div>
                  </div>
                </div>
                <DialogFooter className="gap-2 sm:gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setCancelDialog({ open: false, booking: null, step: 'confirm', reason: '', submitting: false, preview: null, previewLoading: false })}
                    data-testid="business-cancel-no-btn"
                  >
                    {language === 'es' ? 'No, mantener' : 'No, keep it'}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => setCancelDialog((s) => ({ ...s, step: 'reason' }))}
                    data-testid="business-cancel-yes-btn"
                  >
                    {language === 'es' ? 'Si, cancelar' : 'Yes, cancel'}
                  </Button>
                </DialogFooter>
              </>
            )}
            {cancelDialog.step === 'reason' && (
              <>
                <DialogHeader>
                  <DialogTitle>{language === 'es' ? 'Motivo de la cancelación' : 'Cancellation reason'}</DialogTitle>
                  <DialogDescription>
                    {language === 'es'
                      ? 'Cuéntale al cliente por qué tienes que cancelar. Este texto se incluirá en su correo de cancelación.'
                      : 'Tell the client why you have to cancel. This text will be included in their cancellation email.'}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {(language === 'es'
                      ? ['Emergencia personal', 'Enfermedad del colaborador', 'Problema tecnico', 'Cambio de horario imprevisto']
                      : ['Personal emergency', 'Worker illness', 'Technical issue', 'Unexpected schedule change']
                    ).map((preset) => (
                      <Button
                        key={preset}
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 text-[11px] px-2"
                        onClick={() => setCancelDialog((s) => ({ ...s, reason: preset }))}
                        data-testid={`cancel-reason-preset-${preset.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {preset}
                      </Button>
                    ))}
                  </div>
                  <Textarea
                    value={cancelDialog.reason}
                    onChange={(e) => setCancelDialog((s) => ({ ...s, reason: e.target.value }))}
                    placeholder={language === 'es' ? 'Ej: Tuvimos una emergencia familiar y no podremos atenderte hoy. Disculpa las molestias.' : 'Eg: We had a family emergency and cannot serve you today. Sorry for the inconvenience.'}
                    rows={4}
                    maxLength={400}
                    autoFocus
                    data-testid="cancel-reason-textarea"
                  />
                  <p className="text-[11px] text-muted-foreground text-right">{(cancelDialog.reason || '').length}/400</p>
                </div>
                <DialogFooter className="gap-2 sm:gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setCancelDialog((s) => ({ ...s, step: 'confirm' }))}
                    disabled={cancelDialog.submitting}
                    data-testid="cancel-reason-back-btn"
                  >
                    {language === 'es' ? 'Atras' : 'Back'}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={submitBusinessCancellation}
                    disabled={cancelDialog.submitting || (cancelDialog.reason || '').trim().length < 5}
                    data-testid="cancel-reason-submit-btn"
                  >
                    {cancelDialog.submitting
                      ? (language === 'es' ? 'Cancelando...' : 'Cancelling...')
                      : (language === 'es' ? 'Confirmar cancelación' : 'Confirm cancellation')}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Reschedule Modal */}
        <Dialog open={rescheduleModal.open} onOpenChange={(open) => !open && setRescheduleModal({ open: false, booking: null })}>
          <DialogContent className="max-w-md" data-testid="reschedule-modal">
            <DialogHeader>
              <DialogTitle>{language === 'es' ? 'Reagendar Cita' : 'Reschedule Appointment'}</DialogTitle>
              <DialogDescription>
                {rescheduleModal.booking && (
                  <span>
                    {rescheduleModal.booking.user_name || rescheduleModal.booking.client_name} — {rescheduleModal.booking.service_name}
                    <br />
                    {language === 'es' ? 'Fecha actual:' : 'Current date:'} {rescheduleModal.booking.date} {rescheduleModal.booking.time}
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-sm font-medium mb-2 block">{language === 'es' ? 'Nueva fecha' : 'New date'}</Label>
                <Calendar
                  mode="single"
                  selected={rescheduleDate}
                  onSelect={(date) => {
                    setRescheduleDate(date);
                    setRescheduleTime('');
                    if (date) loadRescheduleSlots(date);
                  }}
                  disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                  locale={language === 'es' ? es : enUS}
                  className="rounded-md border mx-auto"
                />
              </div>

              {rescheduleDate && (
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    {language === 'es' ? 'Horario disponible' : 'Available time'} — {format(rescheduleDate, 'dd MMM yyyy', { locale: language === 'es' ? es : enUS })}
                  </Label>
                  {slotsLoading ? (
                    <div className="flex gap-2 flex-wrap">
                      {[1,2,3,4].map(i => <Skeleton key={i} className="h-9 w-20" />)}
                    </div>
                  ) : rescheduleSlots.length > 0 ? (
                    <div className="flex gap-2 flex-wrap max-h-40 overflow-y-auto">
                      {rescheduleSlots.map(slot => (
                        <Button
                          key={slot.time}
                          size="sm"
                          variant={rescheduleTime === slot.time ? 'default' : 'outline'}
                          className="h-9 text-xs"
                          onClick={() => setRescheduleTime(slot.time)}
                          data-testid={`slot-${slot.time}`}
                        >
                          {slot.time}
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">{language === 'es' ? 'No hay horarios disponibles para esta fecha' : 'No available slots for this date'}</p>
                  )}
                </div>
              )}

              <Button
                className="w-full"
                disabled={!rescheduleDate || !rescheduleTime || rescheduleLoading}
                onClick={handleReschedule}
                data-testid="confirm-reschedule-btn"
              >
                {rescheduleLoading
                  ? (language === 'es' ? 'Reagendando...' : 'Rescheduling...')
                  : (language === 'es' ? 'Confirmar reagendamiento' : 'Confirm reschedule')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Booking Detail Modal */}
        <Dialog open={!!bookingDetail} onOpenChange={(open) => { if (!open) { setBookingDetail(null); setClientHistory(null); } }}>
          <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="booking-detail-modal">
            <DialogHeader>
              <DialogTitle>{language === 'es' ? 'Detalle del cliente' : 'Client detail'}</DialogTitle>
              <DialogDescription>
                {language === 'es' ? 'Información del cliente y su historial' : 'Client information and history'}
              </DialogDescription>
            </DialogHeader>
            {bookingDetail && (() => {
              const b = bookingDetail;
              const name = b.client_name || b.user_name || (language === 'es' ? 'Sin nombre' : 'No name');
              const email = b.client_email || b.user_email;
              const phone = b.client_phone || b.user_phone;
              return (
                <div className="space-y-4 mt-1">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#F05D5E]/10 flex items-center justify-center text-[#F05D5E] font-bold text-lg">
                      {name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold">{name}</p>
                      <Badge className={`text-[10px] ${getStatusColor(b.status)}`}>
                        {b.status === 'cancelled' && b.cancelled_by
                          ? (language === 'es' ? `Cancelada por ${b.cancelled_by === 'business' ? 'negocio' : 'cliente'}` : `Cancelled by ${b.cancelled_by}`)
                          : t(`status.${b.status}`)}
                      </Badge>
                    </div>
                  </div>
                  <Separator />
                  <div className="grid gap-2.5 text-sm">
                    {email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                        <a href={`mailto:${email}`} className="text-blue-600 hover:underline truncate">{email}</a>
                      </div>
                    )}
                    {phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                        <a href={`tel:${phone}`} className="text-blue-600 hover:underline">{phone}</a>
                      </div>
                    )}
                    {!email && !phone && (
                      <p className="text-muted-foreground text-xs italic">{language === 'es' ? 'No hay datos de contacto disponibles' : 'No contact info available'}</p>
                    )}
                  </div>
                  <Separator />
                  <div className="grid gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{language === 'es' ? 'Servicio' : 'Service'}</span>
                      <span className="font-medium">{b.service_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{language === 'es' ? 'Profesional' : 'Professional'}</span>
                      <span className="font-medium">{b.worker_name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{language === 'es' ? 'Fecha' : 'Date'}</span>
                      <span className="font-medium">{new Date(b.date + 'T12:00:00').toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{language === 'es' ? 'Horario' : 'Time'}</span>
                      <span className="font-medium">{b.time} - {b.end_time}</span>
                    </div>
                    {b.deposit_amount > 0 ? (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{language === 'es' ? 'Anticipo' : 'Deposit'}</span>
                        <span className="font-medium">{b.deposit_paid ? '✓' : '✗'} ${b.deposit_amount} MXN</span>
                      </div>
                    ) : !biz?.requires_deposit && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{language === 'es' ? 'Tipo de cobro' : 'Payment type'}</span>
                        <span className="font-medium text-blue-600">
                          {language === 'es' ? 'En el local' : 'At location'}
                        </span>
                      </div>
                    )}
                    {(b.booked_by === 'business' || (b.skip_payment && b.client_name)) && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{language === 'es' ? 'Origen' : 'Source'}</span>
                        <div className="flex gap-1">
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">
                            {language === 'es' ? 'Recepcion' : 'Reception'}
                          </span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
                            {language === 'es' ? 'Pago en negocio' : 'Pay at business'}
                          </span>
                        </div>
                      </div>
                    )}
                    {b.total_amount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total</span>
                        <span className="font-medium">${b.total_amount} MXN</span>
                      </div>
                    )}
                    {b.notes && (
                      <div className="mt-1">
                        <p className="text-muted-foreground text-xs mb-1">{language === 'es' ? 'Notas' : 'Notes'}</p>
                        <p className="text-sm bg-muted/50 p-2 rounded">{b.notes}</p>
                      </div>
                    )}
                  </div>
                  {/* Client History */}
                  <Separator />
                  <div>
                    <p className="text-sm font-semibold mb-3">{language === 'es' ? 'Historial del cliente' : 'Client history'}</p>
                    {historyLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="h-5 w-5 border-2 border-[#F05D5E] border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : clientHistory ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-2">
                          <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-2.5 text-center">
                            <p className="text-lg font-bold text-emerald-700 dark:text-emerald-400">{clientHistory.total_visits}</p>
                            <p className="text-[10px] text-muted-foreground">{language === 'es' ? 'Visitas' : 'Visits'}</p>
                          </div>
                          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2.5 text-center">
                            <p className="text-lg font-bold text-blue-700 dark:text-blue-400">${clientHistory.total_spent}</p>
                            <p className="text-[10px] text-muted-foreground">{language === 'es' ? 'Gastado' : 'Spent'}</p>
                          </div>
                          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-2.5 text-center">
                            <p className="text-lg font-bold text-red-600 dark:text-red-400">{clientHistory.total_cancelled}</p>
                            <p className="text-[10px] text-muted-foreground">{language === 'es' ? 'Canceladas' : 'Cancelled'}</p>
                          </div>
                        </div>
                        {clientHistory.history.length > 0 && (
                          <div className="space-y-1.5 max-h-48 overflow-y-auto">
                            {clientHistory.history.map((h, i) => (
                              <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-md bg-muted/30 text-xs" data-testid={`history-item-${i}`}>
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${h.status === 'completed' ? 'bg-emerald-500' : h.status === 'confirmed' ? 'bg-blue-500' : h.status === 'cancelled' ? 'bg-red-500' : 'bg-gray-400'}`} />
                                  <span className="truncate">{h.service_name}</span>
                                </div>
                                <div className="flex items-center gap-2 shrink-0 ml-2">
                                  <span className="text-muted-foreground">{new Date(h.date + 'T12:00:00').toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { day: 'numeric', month: 'short' })}</span>
                                  {h.amount > 0 && <span className="font-medium">${h.amount}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {clientHistory.first_visit && (
                          <p className="text-[10px] text-muted-foreground text-center">
                            {language === 'es' ? 'Cliente desde' : 'Client since'} {new Date(clientHistory.first_visit + 'T12:00:00').toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { month: 'long', year: 'numeric' })}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground text-center py-2">{language === 'es' ? 'Sin historial disponible' : 'No history available'}</p>
                    )}
                  </div>

                  {/* Quick actions — sticky bottom row so the business can act
                      directly from the detail modal without hunting for the
                      tiny buttons in the timeline. */}
                  {(b.status === 'confirmed' || b.status === 'hold') && (
                    <div className="sticky bottom-0 -mx-6 px-6 pt-3 pb-4 bg-background border-t flex flex-col sm:flex-row gap-2">
                      {b.status === 'confirmed' && hasPermission('complete_bookings') && (
                        <Button
                          onClick={async () => {
                            hapticLight();
                            await handleBookingAction(b.id, 'complete');
                            setBookingDetail(null);
                            setClientHistory(null);
                          }}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-11 rounded-full gap-2"
                          data-testid="detail-modal-complete-btn"
                        >
                          <CheckCircle2 className="h-4 w-4" />
                          {language === 'es' ? 'Marcar como completada' : 'Mark as completed'}
                        </Button>
                      )}
                      {hasPermission('cancel_bookings') && (
                        <Button
                          variant="outline"
                          onClick={async () => {
                            // handleBookingAction fires hapticMedium and opens
                            // the cancel confirm dialog (with reason + refund
                            // preview). We don't close the detail modal here —
                            // the cancel flow will refresh state on submit.
                            await handleBookingAction(b.id, 'cancel');
                          }}
                          className="flex-1 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 h-11 rounded-full gap-2 dark:border-red-800 dark:hover:bg-red-950/50"
                          data-testid="detail-modal-cancel-btn"
                        >
                          <XCircle className="h-4 w-4" />
                          {language === 'es' ? 'Cancelar cita' : 'Cancel booking'}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Manager Permissions Modal */}
        <Dialog open={managerModal.open} onOpenChange={(open) => !open && setManagerModal({ open: false, worker: null })}>
          <DialogContent className="max-w-md" data-testid="manager-permissions-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCog className="h-5 w-5 text-amber-500" />
                {managerModal.worker?.is_manager
                  ? (language === 'es' ? 'Editar permisos de administrador' : 'Edit administrator permissions')
                  : (language === 'es' ? 'Designar como administrador' : 'Designate as administrator')}
              </DialogTitle>
              <DialogDescription>
                {managerModal.worker?.name} — {language === 'es'
                  ? 'Selecciona las acciones que este administrador puede realizar'
                  : 'Select the actions this administrator can perform'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-5 mt-2 max-h-[50vh] overflow-y-auto pr-1">
              {Object.entries(PERMISSION_GROUPS[language] || PERMISSION_GROUPS.es).map(([groupName, permKeys]) => (
                <div key={groupName}>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{groupName}</p>
                  <div className="space-y-2">
                    {permKeys.map(key => (
                      <div key={key} className="flex items-center justify-between p-2.5 rounded-lg border border-border/50 hover:bg-muted/30 transition-colors" data-testid={`perm-${key}`}>
                        <Label className="text-sm cursor-pointer flex-1" htmlFor={`perm-switch-${key}`}>
                          {PERMISSION_LABELS[key]?.[language] || key}
                        </Label>
                        <Switch
                          id={`perm-switch-${key}`}
                          checked={!!managerPermissions[key]}
                          onCheckedChange={(checked) => setManagerPermissions(prev => ({ ...prev, [key]: checked }))}
                          data-testid={`switch-${key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter className="gap-2 mt-2">
              <Button variant="outline" onClick={() => setManagerModal({ open: false, worker: null })} data-testid="cancel-manager-modal">
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button
                className="btn-coral"
                onClick={managerModal.worker?.is_manager ? handleUpdatePermissions : handleDesignateManager}
                data-testid="save-manager-btn"
              >
                <CheckCircle2 className="h-4 w-4 mr-1.5" />
                {managerModal.worker?.is_manager
                  ? (language === 'es' ? 'Guardar permisos' : 'Save permissions')
                  : (language === 'es' ? 'Designar administrador' : 'Designate administrator')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* PIN Setup Modal */}
        <Dialog open={pinModal.open} onOpenChange={(open) => { if (!open) { setPinModal({ open: false, type: null }); setPinValue(''); setPinConfirm(''); } }}>
          <DialogContent className="max-w-sm" data-testid="pin-setup-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-amber-500" />
                {pinModal.type === 'owner_setup'
                  ? (language === 'es' ? 'PIN de seguridad del dueño' : 'Owner security PIN')
                  : (language === 'es' ? `PIN para ${pinModal.workerName || 'administrador'}` : `PIN for ${pinModal.workerName || 'administrator'}`)}
              </DialogTitle>
              <DialogDescription>
                {language === 'es'
                  ? 'Ingresa un PIN numérico de 4 a 6 dígitos'
                  : 'Enter a numeric PIN of 4 to 6 digits'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-sm mb-1.5 block">{language === 'es' ? 'Nuevo PIN' : 'New PIN'}</Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="••••"
                  value={pinValue}
                  onChange={(e) => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  data-testid="pin-input"
                  data-no-capitalize="true"
                />
              </div>
              <div>
                <Label className="text-sm mb-1.5 block">{language === 'es' ? 'Confirmar PIN' : 'Confirm PIN'}</Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="••••"
                  value={pinConfirm}
                  onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  data-testid="pin-confirm-input"
                  data-no-capitalize="true"
                />
              </div>
              {pinValue && pinConfirm && pinValue !== pinConfirm && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <XCircle className="h-3 w-3" />
                  {language === 'es' ? 'Los PINs no coinciden' : 'PINs do not match'}
                </p>
              )}
              {pinValue && pinValue.length >= 4 && pinValue === pinConfirm && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  {language === 'es' ? 'PINs coinciden' : 'PINs match'}
                </p>
              )}
            </div>
            <DialogFooter className="gap-2 mt-2">
              <Button variant="outline" onClick={() => { setPinModal({ open: false, type: null }); setPinValue(''); setPinConfirm(''); }} data-testid="cancel-pin-modal">
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button
                className="btn-coral"
                disabled={pinValue.length < 4 || pinValue !== pinConfirm}
                onClick={pinModal.type === 'owner_setup' ? handleSaveOwnerPin : handleSetManagerPin}
                data-testid="save-pin-btn"
              >
                <Shield className="h-4 w-4 mr-1.5" />
                {language === 'es' ? 'Guardar PIN' : 'Save PIN'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Sistema de ayuda contextual ─────────────────────────────── */}
        {/* Solo se activa en español y para el dueño (no managers). */}
        {language === 'es' && !isManager ? (
          <>
            <FloatingHelpButton />
            <GuidedTour />
          </>
        ) : null}
      </div>
    </div>
  );
}
